import { t } from '@shared/helpers/translation';
import { isElementVisible, scrollToElement } from '@shared/helpers/scroll';
import { isEmpty } from 'lodash';
import { useStore } from '@/store';
import { getAgeRange } from '@/api/Cart/cart.helpers';
import { MainProduct, MainProductPax } from '@shared/interfaces/product';
import {
    AgeRange,
    AgeRangeDefinitions,
    Gender,
    PassengerType,
    Disclaimer,
} from '@/api/Checkout/checkout.interfaces';
import { isDate, isDateBetween } from '@ui/components/FormV2/Validations/functions';
import {
    convertToDate,
    getDifferenceInYears,
    isAfter,
    today,
} from '@shared/helpers/datetime/datetime';

export function validRequiredFormValues(
    obj: { [key: string]: boolean },
    requiredValues: string[]
) {
    return Object.entries(obj)
        .map(([k, v]) => ({ key: k, value: v }))
        .filter((item) => requiredValues.includes(item.key))
        .every((item) => item.value);
}

export function scrollToInvalidState() {
    const sElement = document.querySelector(`[data-step-valid=false]`);
    const fElement = document.querySelector(`[field-error='true']`);

    if (fElement) {
        !isElementVisible(fElement, 100) && scrollToElement(fElement, -100, true);
    } else if (sElement) {
        !isElementVisible(sElement, 100) && scrollToElement(sElement, -32);
    }
}

export function scrollToInvalidSection() {
    const elem = document.querySelector(`[data-step-valid=false]`);
    elem && !isElementVisible(elem, 100) && scrollToElement(elem, -100, true);
}

export function scrollToInvalidField() {
    const elem = document.querySelector(`[field-error='true']`);
    elem && !isElementVisible(elem, 100) && scrollToElement(elem, -32);
}

export function sortDisclaimer(items: Disclaimer[] | null) {
    const DisclaimersWithCheckbox = items && items.filter((d) => d.IsCheckbox);
    const DisclaimersWithText = items && items.filter((d) => !d.IsCheckbox);

    return {
        DisclaimersWithCheckbox,
        DisclaimersWithText,
    };
}

export function getRequiredValues(disclaimers: Disclaimer[] | null): string[] {
    if (disclaimers && !isEmpty(disclaimers)) {
        return disclaimers.filter((d) => d.IsCheckbox).map((d) => d.Id);
    }

    return [];
}

function getGenderArray(genderObj: Gender, isAdult: boolean) {
    return Object.entries(genderObj).map(([key]) => {
        return {
            label: isAdult
                ? t({ key: `shared.gender_titles.adult.${key}` })
                : t({ key: `shared.gender_titles.children.${key}` }),
            value: Number.parseInt(key),
        };
    });
}

export function getGendersByProductAndPax(product: MainProduct, pax: MainProductPax) {
    const store = useStore();

    const ageDefinitions: AgeRangeDefinitions =
        store.getters['content/getCheckoutAgeRangeDefinitions'];

    const ageRange = getAgeRange(product, pax, ageDefinitions);

    const isAdult = ![PassengerType.Child, PassengerType.Infant].includes(ageRange.Type);

    const genderTitles: Gender | null = store.getters['content/getCheckoutGenderTitles'];

    return genderTitles ? getGenderArray(genderTitles, isAdult) : [];
}

export const isValidDateOfBirth = (
    dateOfBirth: string,
    ageRange: AgeRange | null,
    departureDate: Date
): string | boolean => {
    const isValidDate = isDate(dateOfBirth);

    if (isValidDate !== true) {
        return isValidDate;
    }

    if (!ageRange) {
        return isDateBetween(new Date('1800-01-01'), new Date())(dateOfBirth);
    }

    const dob = convertToDate(dateOfBirth);
    const yearsAtDeparture = getDifferenceInYears(dob, departureDate);
    const isValid = yearsAtDeparture >= ageRange.Min && yearsAtDeparture <= ageRange.Max;

    if (isAfter(dob, today())) return t({ key: 'shared.cannot_be_future_date' });
    else if (isAfter(dob, departureDate))
        return t({ key: 'shared.cannot_be_date_after_departure' });
    else if (isValid) {
        return true;
    }

    return getAgeRangeError(yearsAtDeparture, ageRange);
};

function getAgeRangeError(age: number, ageRange: AgeRange): string {
    const toYoung = age < ageRange.Min;
    switch (ageRange.Type) {
        case PassengerType.Infant:
            return toYoung
                ? t({
                      key: 'shared.infant_max_age_during_journey_error',
                      values: [ageRange.Min, age],
                  })
                : t({
                      key: 'shared.infant_min_age_during_journey_error',
                      values: [ageRange.Max + 1, age],
                  });
        case PassengerType.Child:
            return toYoung
                ? t({
                      key: 'shared.child_max_age_during_journey_error',
                      values: [ageRange.Min, age],
                  })
                : t({
                      key: 'shared.child_min_age_during_journey_error',
                      values: [ageRange.Max + 1, age],
                  });
        default:
            return toYoung
                ? t({
                      key: 'shared.adult_min_age_during_journey_error',
                      values: [ageRange.Min, age],
                  })
                : t({
                      key: 'shared.adult_max_age_during_journey_error',
                      values: [ageRange.Max, age],
                  });
    }
}
